import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import debounce from "lodash/debounce";
import moment, { Moment } from "moment";
import toastr from "toastr";

import {
  makeGETRequest,
  makePOSTRequest,
  makePOSTRequestOnUserService,
} from "../../../Api/api";
import IQuoteData from "../../../interfaces/IQuoteData";
import IBondDetails, {
  CallOption,
  Maturity,
} from "../../../interfaces/IBondDetails";
import {
  corporateBonds_moneyMarket_xirr,
  gsec_sdl_xirr,
  zcb_mld_xirr,
} from "../../../Helpers/calc-apis";
import {
  getUpdatedAmountByStampDuty,
  is_gsec_sdl_tBill,
} from "../../../Helpers/functions";

type CashflowPreview = {
  date: string;
  amount: number;
  type: "Interest" | "Maturity";
};

type CalculationResult = {
  investmentAmount: number;
  principalAmount: number;
  accruedInterest: number;
  stampDuty: number;
  accruedDays: number;
  noOfBonds: number;
  Yield: number;
  price: number;
  settlementDate: Moment;
  interestFrequency: string;
};

type FormState = {
  numberOfBonds: string;
  price: string;
  yield: string;
  settlementAmount: string;
  stampDuty: string;
  settlementDate: Moment | null;
  faceValue: string;
  accruedInterest: string;
  source: string;
};

const initialFormState: FormState = {
  faceValue: "",
  numberOfBonds: "",
  settlementAmount: "",
  stampDuty: "",
  settlementDate: null,
  price: "",
  yield: "",
  accruedInterest: "",
  source: "",
};

const formatNumber = (value?: number | null, decimals = 2) => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return "";
  }
  return Number(value).toFixed(decimals);
};

const parseNumericString = (raw?: string | null) => {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const sanitized = trimmed.replace(/[^0-9.-]/g, "");
  if (!sanitized) return null;
  const parsed = Number(sanitized);
  return Number.isFinite(parsed) ? parsed : null;
};

const getFaceValue = (bond?: IBondDetails | null) => {
  if (!bond) return null;
  const faceValue =
    bond.faceValue !== undefined && bond.faceValue !== null
      ? Number(bond.faceValue.toString().replace(/[^0-9.]/g, ""))
      : null;
  const priceFromField = bond.pricePerBond
    ? Number(
        bond.pricePerBond
          .toString()
          .split(" ")[0]
          .replace(/[^0-9.]/g, ""),
      )
    : null;
  if (Number.isFinite(faceValue)) return Number(faceValue);
  if (Number.isFinite(priceFromField)) return Number(priceFromField);
  return null;
};

const getInterestRate = (bond?: IBondDetails | null) => {
  if (!bond?.coupons?.length) return 0;
  const rate = Number(bond.coupons[0].rate);
  return Number.isNaN(rate) ? 0 : rate;
};

const deriveCashflows = (
  dates: string[],
  interests: number[],
  maturities: number[],
  faceValue: number,
) => {
  const list: CashflowPreview[] = [];
  for (let i = 1; i < dates.length; i += 1) {
    const interestComponent = Number(interests[i]);
    const maturityComponent = Number(maturities[i]);
    if (interestComponent) {
      list.push({
        date: dates[i],
        amount: (interestComponent * faceValue) / 100,
        type: "Interest",
      });
    }
    if (maturityComponent) {
      list.push({
        date: dates[i],
        amount: (maturityComponent * faceValue) / 100,
        type: "Maturity",
      });
    }
  }
  return list;
};

const deriveMldCashflows = (
  dates: string[],
  amounts: number[],
  faceValue: number,
) =>
  dates.slice(1).map((date, index) => ({
    date: moment(date, "YYYY-MM-DD").format("DD/MM/YYYY"),
    amount: (Number(amounts[index + 1]) * faceValue) / 100,
    type: "Maturity" as const,
  }));

type AddExternalBondDialogProps = {
  open: boolean;
  onClose: () => void;
  currentUser: any;
  onAdded?: () => void;
};

const AddExternalBondDialog: React.FC<AddExternalBondDialogProps> = ({
  open,
  onClose,
  currentUser,
  onAdded,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<IQuoteData[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<IQuoteData | null>(null);
  const [selectedBond, setSelectedBond] = useState<IBondDetails | null>(null);
  const [isFetchingBond, setIsFetchingBond] = useState(false);

  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calculatedValues, setCalculatedValues] =
    useState<CalculationResult | null>(null);
  const [cashflowPreview, setCashflowPreview] = useState<CashflowPreview[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [touched, setTouched] = useState({
    numberOfBonds: false,
    price: false,
    settlementDate: false,
  });

  const fallbackYieldRef = useRef<number>(0);

  const faceValue = useMemo(() => getFaceValue(selectedBond), [selectedBond]);
  const interestRate = useMemo(
    () => getInterestRate(selectedBond),
    [selectedBond],
  );
  const divisibilityFactor = selectedBond?.divisibilityFactor || 365;
  const securityType = useMemo(() => {
    if (!selectedBond) return "";
    return (
      selectedBond.securityType?.type ||
      selectedBond.securityType?.name ||
      ""
    ).trim();
  }, [selectedBond]);

  const resetState = useCallback(() => {
    setSearchTerm("");
    setSearchResults([]);
    setSelectedQuote(null);
    setSelectedBond(null);
    setFormState(initialFormState);
    setCashflowPreview([]);
    setCalculatedValues(null);
    setSubmitLoading(false);
    setCalcLoading(false);
    setTouched({
      numberOfBonds: false,
      price: false,
      settlementDate: false,
    });
    fallbackYieldRef.current = 0;
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  useEffect(() => {
    const parsed = parseNumericString(formState.yield);
    if (parsed !== null) {
      fallbackYieldRef.current = parsed;
    } else {
      fallbackYieldRef.current = 0;
    }
  }, [formState.yield]);

  const runSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!query.trim()) {
          setSearchResults([]);
          setIsSearching(false);
          return;
        }
        setIsSearching(true);
        try {
          const payload: Record<string, any> = {
            query_string: query.trim(),
            user_id: currentUser?.uuid,
          };
          const name = `${currentUser?.firstName ?? ""} ${
            currentUser?.lastName ?? ""
          }`.trim();
          if (name) payload.name = name;
          if (currentUser?.emailId) payload.email = currentUser.emailId;
          if (currentUser?.smestID) payload.smestid = currentUser.smestID;
          if (currentUser?.mobileNumber)
            payload.mobileNumber = currentUser.mobileNumber;
          const response = (await makePOSTRequest(
            "/admin/security_master/trade_floor/search/",
            payload,
          )) as any;
          setSearchResults(response?.data || []);
        } catch (error: any) {
          if (error?.response?.status === 404) {
            setSearchResults([]);
          } else {
            toastr.error("Unable to fetch search results");
          }
        } finally {
          setIsSearching(false);
        }
      }, 400),
    [currentUser],
  );

  useEffect(() => {
    return () => {
      runSearch.cancel();
    };
  }, [runSearch]);

  const handleSearchInput = (_: any, value: string, reason?: string) => {
    const nextValue = value ?? "";
    if (reason === "clear") {
      setSearchTerm("");
      setSearchResults([]);
      return;
    }
    if (reason && reason !== "input") return;
    setSearchTerm(nextValue);
    if (!nextValue) {
      setSearchResults([]);
      return;
    }
    runSearch(nextValue);
  };

  const getSearchOptionLabel = (option: IQuoteData | string) => {
    if (typeof option === "string") return option;
    const name = option.security_name || "";
    const isin = option.isin || "";
    if (name && isin) return `${name} (${isin})`;
    return name || isin || "";
  };

  const populateDefaultFields = useCallback((bond: IBondDetails) => {
    const fv = getFaceValue(bond);
    const price =
      bond.quoteDetails?.sellPrice &&
      !Number.isNaN(Number(bond.quoteDetails.sellPrice))
        ? Number(bond.quoteDetails.sellPrice)
        : "";
    const yieldValue =
      bond.quoteDetails?.sellYield &&
      !Number.isNaN(Number(bond.quoteDetails.sellYield))
        ? Number(bond.quoteDetails.sellYield)
        : "";
    const settlementDate = bond.quoteDetails?.settlementDate
      ? moment(bond.quoteDetails.settlementDate)
      : bond.sellOrderSettlementDate
        ? moment(bond.sellOrderSettlementDate)
        : null;

    setFormState((prev) => ({
      ...prev,
      faceValue: fv ? String(fv) : prev.faceValue,
      price: price ? String(price) : "",
      yield: yieldValue ? formatNumber(yieldValue, 4) : "",
      settlementDate,
      numberOfBonds: "",
      settlementAmount: "",
      stampDuty: "",
      accruedInterest: "",
      source: "",
    }));
    setCashflowPreview([]);
    setCalculatedValues(null);
    fallbackYieldRef.current = yieldValue || 0;
  }, []);

  const handleSelectResult = useCallback(
    async (result: IQuoteData | null) => {
      if (!result) return;
      setIsFetchingBond(true);
      try {
        const response = (await makeGETRequest(
          `admin/security_master/get/${result.isin}/`,
        )) as any;
        setSelectedQuote(result);
        setSelectedBond(response?.data || null);
        populateDefaultFields(response?.data);
        setSearchResults([]);
        setSearchTerm("");
      } catch (_error) {
        toastr.error("Unable to fetch bond details");
      } finally {
        setIsFetchingBond(false);
      }
    },
    [populateDefaultFields],
  );

  const updateFormField = (
    field: keyof FormState,
    value: string | Moment | null,
  ) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value as FormState[keyof FormState],
    }));
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
    if (typeof value === "string") {
      setCalculatedValues((prev) => {
        if (!prev) return prev;
        if (field === "yield") {
          const parsedYield = parseNumericString(value);
          if (parsedYield === null) {
            fallbackYieldRef.current = 0;
            return prev;
          }
          fallbackYieldRef.current = parsedYield;
          return { ...prev, Yield: parsedYield };
        }
        if (field === "settlementAmount") {
          const parsedAmount = parseNumericString(value);
          if (parsedAmount === null) return prev;
          return {
            ...prev,
            investmentAmount: parsedAmount,
            principalAmount: Math.max(parsedAmount - prev.accruedInterest, 0),
          };
        }
        if (field === "accruedInterest") {
          const parsedAccrued = parseNumericString(value);
          if (parsedAccrued === null) return prev;
          return {
            ...prev,
            accruedInterest: parsedAccrued,
            principalAmount: Math.max(prev.investmentAmount - parsedAccrued, 0),
          };
        }
        if (field === "stampDuty") {
          const parsedStampDuty = parseNumericString(value);
          if (parsedStampDuty === null) return prev;
          return {
            ...prev,
            stampDuty: parsedStampDuty,
          };
        }
        if (field === "price") {
          const parsedPrice = parseNumericString(value);
          if (parsedPrice === null) return prev;
          return { ...prev, price: parsedPrice };
        }
        return prev;
      });
    }
  };

  const handleCalculationSuccess = useCallback(
    (
      yieldValue: number,
      priceValue: number,
      accruedDays: number,
      cashflows: CashflowPreview[],
      units: number,
      stampDuty: number,
      investmentAmount: number,
      principalAmount: number,
      accruedInterest: number,
      settlementDate: Moment,
      _ipDates: string[],
      interestFrequency: string,
    ) => {
      fallbackYieldRef.current = yieldValue;
      setCalculatedValues({
        investmentAmount,
        principalAmount,
        accruedInterest,
        stampDuty,
        accruedDays,
        noOfBonds: units,
        Yield: yieldValue,
        price: priceValue,
        settlementDate,
        interestFrequency,
      });
      setFormState((prev) => ({
        ...prev,
        settlementAmount: formatNumber(investmentAmount, 2),
        yield: formatNumber(yieldValue, 4),
        accruedInterest: formatNumber(accruedInterest, 2),
        stampDuty: formatNumber(stampDuty, 2),
      }));
      setCashflowPreview(cashflows);
    },
    [],
  );

  const displayUsageLimitMessage = useCallback(() => {
    toastr.error(
      "Unable to calculate yield right now. Please try again in a bit.",
    );
  }, []);

  const executeCalculation = useCallback(
    async (units: number, priceValue: number, settlementDate: Moment) => {
      if (!selectedBond || !faceValue) return;

      try {
        setCalcLoading(true);
        let response;

        if (
          securityType === "Corporate Bonds" ||
          securityType === "Corporate Bond"
        ) {
          response = await corporateBonds_moneyMarket_xirr(
            selectedBond,
            priceValue,
            settlementDate,
            false,
            units,
          );
          if (
            response &&
            response.status === 200 &&
            !response.data.hasOwnProperty("message")
          ) {
            const derivedYield =
              (response.data.xirr ??
                response.data.Simple_Interest ??
                fallbackYieldRef.current) ||
              0;
            const marketValue = (faceValue * priceValue) / 100;
            const accruedDays = Number(response.data.cashflow?.Days?.[0]) || 0;
            const accruedInterestPerUnit =
              (((faceValue * interestRate) / 100) * accruedDays) /
              (divisibilityFactor || 365);
            let investmentAmount =
              units * (marketValue + accruedInterestPerUnit);
            const stampDuty =
              getUpdatedAmountByStampDuty(investmentAmount) ?? 0;
            investmentAmount += stampDuty;
            const cashflows = deriveCashflows(
              response.data.cashflow?.AdjustedValueDate || [],
              response.data.cashflow?.Interest || [],
              response.data.cashflow?.Maturity || [],
              faceValue,
            );
            const ipDates =
              response.data.cashflow?.AdjustedValueDate?.map((date: string) =>
                moment(date, "DD/MM/YYYY").format("DD MMM"),
              ) || [];
            const frequencyLabel =
              selectedBond.frequencyOfInterest ||
              selectedBond.frequencyOfInterestOptions ||
              "Not specified";
            handleCalculationSuccess(
              derivedYield,
              priceValue,
              accruedDays,
              cashflows,
              units,
              stampDuty,
              investmentAmount,
              marketValue * units,
              accruedInterestPerUnit * units,
              settlementDate,
              ipDates,
              frequencyLabel,
            );
          } else {
            displayUsageLimitMessage();
          }
        } else if (
          securityType === "Market Linked Debentures" ||
          securityType === "Zero Coupon Bonds"
        ) {
          response = await zcb_mld_xirr(
            selectedBond,
            priceValue,
            settlementDate,
            false,
            units,
          );
          if (
            response &&
            response.status === 200 &&
            !response.data?.hasOwnProperty("message")
          ) {
            const derivedYield =
              response.data?.xirr ?? fallbackYieldRef.current ?? 0;
            const cashflows = deriveMldCashflows(
              response.data.cashflowDf?.dates || [],
              response.data.cashflowDf?.amounts || [],
              faceValue,
            );
            const investmentAmount =
              units * ((faceValue * priceValue) / 100) +
              (getUpdatedAmountByStampDuty(
                units * ((faceValue * priceValue) / 100),
              ) || 0);
            const principalAmount = (faceValue * priceValue * units) / 100;
            handleCalculationSuccess(
              derivedYield,
              priceValue,
              0,
              cashflows,
              units,
              getUpdatedAmountByStampDuty(
                units * ((faceValue * priceValue) / 100),
              ) || 0,
              investmentAmount,
              principalAmount,
              0,
              settlementDate,
              [],
              selectedBond.frequencyOfInterest || "Not specified",
            );
          } else {
            displayUsageLimitMessage();
          }
        } else if (is_gsec_sdl_tBill(securityType)) {
          response = await gsec_sdl_xirr(
            selectedBond,
            priceValue,
            settlementDate,
            false,
            units,
          );
          if (
            response &&
            response.status === 200 &&
            !response.data.hasOwnProperty("message")
          ) {
            const derivedYield =
              (response.data.Yield_Half_Yearly ??
                response.data.Simple_Interest ??
                fallbackYieldRef.current) ||
              0;
            const marketValue = (faceValue * priceValue) / 100;
            const accruedDays = Number(response.data.cashflow?.Days?.[0]) || 0;
            const accruedInterestPerUnit =
              (((faceValue * interestRate) / 100) * accruedDays) /
              (divisibilityFactor || 365);
            const investmentAmount =
              units * (marketValue + accruedInterestPerUnit);
            const stampDuty =
              getUpdatedAmountByStampDuty(investmentAmount) ?? 0;
            const cashflows = deriveCashflows(
              response.data.cashflow?.ValueDate || [],
              response.data.cashflow?.Interest || [],
              response.data.cashflow?.Maturity || [],
              faceValue,
            );
            const ipDates =
              response.data.cashflow?.ValueDate?.map((date: string) =>
                moment(date, "DD/MM/YYYY").format("DD MMM"),
              ) || [];
            const frequencyLabel =
              selectedBond.frequencyOfInterest ||
              selectedBond.frequencyOfInterestOptions ||
              "Not specified";
            handleCalculationSuccess(
              derivedYield,
              priceValue,
              accruedDays,
              cashflows,
              units,
              stampDuty,
              investmentAmount,
              marketValue * units,
              accruedInterestPerUnit * units,
              settlementDate,
              ipDates,
              frequencyLabel,
            );
          } else {
            displayUsageLimitMessage();
          }
        } else {
          toastr.error("Unsupported security type for calculation.");
        }
      } catch (error) {
        toastr.error("Unable to calculate cashflows.");
      } finally {
        setCalcLoading(false);
      }
    },
    [
      divisibilityFactor,
      faceValue,
      handleCalculationSuccess,
      interestRate,
      securityType,
      selectedBond,
      displayUsageLimitMessage,
    ],
  );

  useEffect(() => {
    const units = parseNumericString(formState.numberOfBonds);
    const priceValue = parseNumericString(formState.price);
    const settlementDate = formState.settlementDate;

    if (!selectedBond || !units || !priceValue || !settlementDate) {
      setCalculatedValues(null);
      setCashflowPreview([]);
      return;
    }

    executeCalculation(units, priceValue, settlementDate);
  }, [
    executeCalculation,
    formState.numberOfBonds,
    formState.price,
    formState.settlementDate,
    selectedBond,
  ]);

  const handleSubmit = async () => {
    if (!selectedBond || !calculatedValues || !cashflowPreview.length) {
      toastr.error("Please complete the calculation before submitting.");
      setTouched((prev) => ({
        ...prev,
        numberOfBonds: true,
        price: true,
        settlementDate: true,
      }));
      return;
    }
    const noOfBonds =
      calculatedValues.noOfBonds ||
      parseNumericString(formState.numberOfBonds) ||
      0;
    const faceValueForPayload =
      parseNumericString(formState.faceValue) ?? faceValue ?? 0;
    const finalYield =
      calculatedValues.Yield ??
      parseNumericString(formState.yield) ??
      fallbackYieldRef.current ??
      0;
    const finalPrice =
      calculatedValues.price ??
      parseNumericString(formState.price) ??
      Number(formState.price) ??
      0;
    const settlementMoment =
      formState.settlementDate ?? calculatedValues.settlementDate;
    if (!noOfBonds || !faceValueForPayload || !settlementMoment) {
      toastr.error("Missing required fields to create the order.");
      return;
    }
    setSubmitLoading(true);
    try {
      const lastCallDate =
        selectedBond.callOptions?.length &&
        selectedBond.callOptions
          .slice()
          .sort(
            (a: CallOption, b: CallOption) =>
              moment(a.date, "DD/MM/YYYY").valueOf() -
              moment(b.date, "DD/MM/YYYY").valueOf(),
          )
          .at(-1)?.date;
      const lastMaturityDate =
        selectedBond.maturities?.length &&
        selectedBond.maturities
          .slice()
          .sort(
            (a: Maturity, b: Maturity) =>
              moment(a.date, "DD/MM/YYYY").valueOf() -
              moment(b.date, "DD/MM/YYYY").valueOf(),
          )
          .at(-1)?.date;

      const ratingMaster = selectedBond.ratingOrganizationMasters?.[0];

      const payload = {
        user: currentUser?.uuid,
        isin: selectedBond.isin,
        rating:
          selectedQuote?.rating ||
          ratingMaster?.rating?.rating ||
          ratingMaster?.rating?.name,
        rating_agency:
          selectedQuote?.ratings_org_name ||
          selectedQuote?.organization_name ||
          ratingMaster?.organization?.name ||
          ratingMaster?.organization?.type,
        value: Number((faceValueForPayload * noOfBonds).toFixed(2)),
        minimum_order_value: Number(
          (faceValueForPayload * noOfBonds).toFixed(2),
        ),
        sell_yield: Number(finalYield.toFixed(4)),
        sell_price: Number(finalPrice.toFixed(4)),
        no_of_bonds: noOfBonds,
        modified_accrued_interest: Number(
          calculatedValues.accruedInterest.toFixed(2),
        ),
        total_consideration: Number(
          calculatedValues.investmentAmount.toFixed(2),
        ),
        principal_amount: Number(calculatedValues.principalAmount.toFixed(2)),
        settlement_date: settlementMoment.clone().format("MMM DD,YYYY"),
        security_name: selectedBond.securityName,
        cashflow_records: cashflowPreview.map((item) => ({
          date: item.date,
          amount: (Number(item.amount) * noOfBonds).toFixed(4),
          type: item.type,
        })),
        s3_url:
          selectedBond.issuerLogo || selectedBond.issuerOfSecurity?.s3Url || "",
        last_call_date: lastCallDate || null,
        last_maturity_date: lastMaturityDate || null,
        source: formState.source.trim(),
      };

      const response = (await makePOSTRequestOnUserService(
        "/order_management_v2/external_order_create/",
        payload,
      )) as any;
      if (response?.status === 200 || response?.status === 201) {
        toastr.success("Bond added to portfolio.");
        onAdded?.();
        onClose();
        resetState();
      }
    } catch (_error) {
      toastr.error("Unable to add bond to portfolio.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const calculationReady =
    !!selectedBond &&
    !!calculatedValues &&
    !!formState.numberOfBonds &&
    !!formState.price &&
    !!formState.settlementDate;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Add External Bond</DialogTitle>
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={searchResults}
                loading={isSearching || isFetchingBond}
                getOptionLabel={getSearchOptionLabel}
                filterOptions={(options) => options}
                openOnFocus
                noOptionsText={
                  searchTerm ? "No matches found." : "Start typing to search."
                }
                onInputChange={handleSearchInput}
                inputValue={searchTerm}
                onChange={(_e, value) => {
                  if (!value || typeof value === "string") return;
                  handleSelectResult(value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search by name or ISIN"
                    placeholder="Start typing to search"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isSearching ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    {typeof option === "string" ? (
                      option
                    ) : (
                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>
                          {option.security_name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {option.isin} - {option.rating}
                        </Typography>
                      </Box>
                    )}
                  </li>
                )}
              />
            </Grid>

            {selectedBond && (
              <>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {selectedBond.securityName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedBond.issuerOfSecurity?.issuerName} -{" "}
                      {selectedBond.securityType?.type ||
                        selectedBond.securityType?.name}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Face Value"
                    fullWidth
                    value={formState.faceValue}
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Coupon / Yield"
                    fullWidth
                    value={
                      selectedQuote?.sell_yield ||
                      selectedBond.coupons?.[0]?.rate ||
                      "-"
                    }
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Number of Bonds"
                    fullWidth
                    value={formState.numberOfBonds}
                    onChange={(e) =>
                      updateFormField("numberOfBonds", e.target.value)
                    }
                    required
                    size="small"
                    error={touched.numberOfBonds && !formState.numberOfBonds}
                    helperText={
                      touched.numberOfBonds && !formState.numberOfBonds
                        ? "Required"
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Price (%)"
                    fullWidth
                    value={formState.price}
                    onChange={(e) => updateFormField("price", e.target.value)}
                    required
                    size="small"
                    error={touched.price && !formState.price}
                    helperText={
                      touched.price && !formState.price ? "Required" : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Settlement Date"
                    value={formState.settlementDate}
                    onChange={(value) =>
                      updateFormField("settlementDate", value)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        required
                        error={
                          touched.settlementDate && !formState.settlementDate
                        }
                        helperText={
                          touched.settlementDate && !formState.settlementDate
                            ? "Required"
                            : params?.helperText
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Calculated Yield"
                    fullWidth
                    value={formState.yield}
                    onChange={(e) => updateFormField("yield", e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Source (optional)"
                    fullWidth
                    value={formState.source}
                    onChange={(e) => updateFormField("source", e.target.value)}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Settlement Amount"
                    fullWidth
                    value={formState.settlementAmount}
                    onChange={(e) =>
                      updateFormField("settlementAmount", e.target.value)
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Accrued Interest"
                    fullWidth
                    value={formState.accruedInterest}
                    onChange={(e) =>
                      updateFormField("accruedInterest", e.target.value)
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Stamp Duty"
                    fullWidth
                    value={formState.stampDuty}
                    onChange={(e) =>
                      updateFormField("stampDuty", e.target.value)
                    }
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Investment Amount
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {calculatedValues
                          ? formatNumber(calculatedValues.investmentAmount, 2)
                          : "--"}
                      </Typography>
                    </Box>
                    {calcLoading && <CircularProgress size={20} />}
                  </Box>
                </Grid>

                {cashflowPreview.length > 0 && (
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      Cashflow Preview
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: 240,
                        overflowY: "auto",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cashflowPreview.map((cf, idx) => (
                            <TableRow key={`${cf.date}-${cf.type}-${idx}`}>
                              <TableCell>
                                {moment(cf.date, [
                                  "DD/MM/YYYY",
                                  "YYYY-MM-DD",
                                ]).format("DD MMM, YYYY")}
                              </TableCell>
                              <TableCell>{cf.type}</TableCell>
                              <TableCell align="right">
                                {formatNumber(cf.amount, 2)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!calculationReady || submitLoading}
        >
          {submitLoading ? <CircularProgress size={18} /> : "Add Bond"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddExternalBondDialog;
